<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>

    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-2 mb-2 mb-xl-0 font-weight-bold">表單管理</h4>
        <div
          class="
            col-12 col-xl-8
            d-flex
            flex-column flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            v-if="checkPermission([consts.FORM_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            :to="{ name: 'FormCreate' }"
            ><i class="fa fa-plus"></i>新增表單</b-button
          >
          <b-form-select
            class="ml-2"
            v-model="query.type"
            :options="typeOptions"
            @change="handlePageReset"
          ></b-form-select>
          <b-form-select
            class="ml-2"
            v-model="query.is_processing"
            :options="isProcessingOptions"
            @change="handlePageReset"
          ></b-form-select>
          <b-form-select
            class="ml-2"
            v-model="query.status"
            :options="statusOptions"
            @change="handlePageReset"
          ></b-form-select>

          <b-input-group class="ml-0 ml-xl-4">
            <b-form-input
              v-model="query.keyword"
              placeholder="搜尋"
              v-on:keyup.enter="handlePageReset"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageReset">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="forms"
                :fields="fields"
                @sort-changed="handleSort"
              >
                <template #cell(start_end_time)="data">
                  <div>
                    {{ dateFormatter(data.item.start_at) }}
                  </div>
                  <div>
                    {{ dateFormatter(data.item.end_at) }}
                  </div>
                </template>
                <template #cell(is_processing)="data">
                  <b-badge
                    :variant="data.item.is_processing ? 'success' : 'warning'"
                  >
                    {{ data.item.is_processing ? "生效中" : "已失效" }}
                  </b-badge>
                </template>
                <template #cell(status)="data">
                  <b-badge v-if="data.item.status == 'draft'" variant="warning">
                    草稿
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'published'"
                    variant="success"
                  >
                    公開
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'closed'"
                    variant="secondary"
                  >
                    關閉
                  </b-badge>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.FORM_EDIT])"
                    variant="inverse-dark"
                    @click="showFormUrl(data.item)"
                  >
                    <i class="fa fa-link"></i>
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.FORM_RESULT])"
                    variant="inverse-info"
                    :to="{
                      name: 'FormResultList',
                      params: { id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-note-text"></span> 填寫內容
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.FORM_CREATE])"
                    variant="inverse-info"
                    @click="copyFormData(data.item)"
                  >
                    <span class="mdi mdi-content-copy"></span> 複製
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.FORM_EDIT])"
                    variant="inverse-primary"
                    :to="{ name: 'FormRead', params: { id: data.item.id } }"
                  >
                    <span class="mdi mdi-eye"></span> 查看
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.FORM_EDIT])"
                    variant="inverse-warning"
                    :to="{ name: 'FormEdit', params: { id: data.item.id } }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.FORM_DELETE])"
                    variant="inverse-danger"
                    @click="deleteForm(data.item)"
                  >
                    <span class="mdi mdi-delete"></span> 刪除
                  </b-button>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>

    <FormUrlModal
      :form="form"
      :formTitle="formTitle"
      :formUrl="formUrl"
      :checkinUrl="checkinUrl"
    />
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import formApi from "@/apis/form";
import { mapState } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import FormUrlModal from "./FormUrlModal.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, FormUrlModal, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    return {
      consts: consts,
      showLoading: false,
      forms: [],
      statusOptions: [
        { value: null, text: "全部表單" },
        { value: "draft", text: "草稿" },
        { value: "published", text: "公開" },
        { value: "closed", text: "關閉" },
      ],
      isProcessingOptions: [
        { text: "全部狀態", value: null },
        { text: "生效中", value: true },
        { text: "已失效", value: false },
      ],
      fields: [
        { key: "title", label: "表單標題", sortable: true },
        {
          key: "type",
          label: "類型",
          formatter: (value) => {
            if (value === "survey") {
              return "問券";
            } else if (value === "event") {
              return "活動";
            } else if (value === "apply") {
              return "申請";
            } else {
              return "";
            }
          },
        },
        {
          key: "is_processing",
          label: "生效狀態",
          formatter: (value, key, item) => {
            const now = new Date();
            const startAt = new Date(item.start_at);
            const endAt = new Date(item.end_at);

            item.is_processing =
              startAt <= now && endAt >= now && item.status === "published";

            return item.is_processing;
          },
        },
        { key: "status", label: "狀態", sortable: true },
        { key: "result_count", label: "填寫數量", sortable: true },
        { key: "start_end_time", label: "開始/結束時間" },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      formTitle: null,
      formUrl: null,
      checkinUrl: null,
      initialized: false,
      query: {
        keyword: "",
        per_page: 10,
        page: 1,
        sort_by: "created_at",
        order_by: "desc",
        type: null,
        status: null,
        is_processing: null,
      },
      form: null,
      module: null,
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    typeOptions() {
      let options = [{ value: null, text: "全部類型" }];

      if (this.checkPermission([consts.FORM_SURVEY_MODE])) {
        options.push({ value: "survey", text: "問券" });
      }
      if (this.checkPermission([consts.FORM_EVENT_MODE])) {
        options.push({ value: "event", text: "活動" });
      }
      if (this.checkPermission([consts.FORM_APPLY_MODE])) {
        options.push({ value: "apply", text: "申請" });
      }

      return options;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      const defaultQuery = {
        type: null,
        status: null,
        is_processing: null,
      };
      this.query = { ...defaultQuery, ...to.query };
      this.fetchForms();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    await this.fetchForms();
    this.initialized = true;
  },
  methods: {
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchForms() {
      this.showLoading = true;
      try {
        const params = { ...this.query };
        let response = await formApi.getForms(params);
        this.forms = response.data.data;
        this.totalRows = response.data.meta.total;
        this.module = response.data.module;
        this.showLoading = false;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取表單列表失敗", "error");
      }
    },
    async deleteForm(form) {
      try {
        this.showLoading = true;
        const result = await this.$swal({
          title: "你確定要刪除嗎？",
          html: `<br/>表單標題：${form.title}<br/><br/>刪除後，相關資料將無法回復，<br/>請您確認！<br/>`,
          type: "warning",
          showConfirmButton: true,
          showCancelButton: true,
          cancelButtonColor: "#3085D7",
          cancelButtonText: "不刪除",
          confirmButtonColor: "#DD3333",
          confirmButtonText: "確定刪除",
          reverseButtons: true,
        });

        if (!result.value) {
          return;
        }

        await formApi.deleteForm(form.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchForms();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
    showFormUrl(form) {
      this.formTitle = form.title;

      // 檢查是否有自定義 form URL
      const customizeUrl = this.module?.module_config?.customize_url;
      if (customizeUrl?.form) {
        this.formUrl = customizeUrl.form.replace('{$hashid}', form.id);
      } else {
        this.formUrl = `https://liff.line.me/${this.organization.liff_id}/form/${form.id}`;
      }

      // 檢查是否有自定義 checkin URL
      if (form.type == "event") {
        if (customizeUrl?.checkin) {
          this.checkinUrl = customizeUrl.checkin.replace('{$hashid}', form.id);
        } else {
          this.checkinUrl = `https://liff.line.me/${this.organization.liff_id}/form/${form.id}/checkin`;
        }
      } else {
        this.checkinUrl = null;
      }

      this.form = form;
      this.$bvModal.show("form-url-modal");
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    dateFormatter: (value) => {
      if (!value) return "無";
      return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
    },
    async copyFormData(form) {
      this.$swal
        .fire({
          type: "warning",
          title: "請問是否複製表單<br>【" + form.title + "】",
          confirmButtonText: "確定複製",
          cancelButtonText: "取消",
          showCancelButton: true,
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.showLoading = true;

              const response = await formApi.copyForm(form.id);
              if (response.status === 200) {
                await this.$swal.fire({
                  title: "成功",
                  type: "success",
                  text: "已複製完成",
                });
                await this.fetchForms();
                this.showLoading = false;
              }
            } catch (error) {
              this.$swal(
                "失敗",
                error.response.data.message,
                "複製失敗，請稍候再試"
              );
            }
          }
        });
    },
  },
};
</script>
